<script lang="ts">
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	import { user_data } from 'src/stores/account/user';
	//Helpers
	import { capFirstChar } from '../../../helpers/utilities';
	//Design system components
	import {
		Collapse,
		toast,
		Loader,
		Stack,
		SelectCard,
		IconButton,
		CardGrid,
		Modal,
		Button,
		Text,
		CircleAvatar,
		Alert,
		Container,
	} from '@Shelly/shelly-design-system';
	import { deleteImageFromStore, handleRequest } from 'src/helpers/upload_image';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const userImagesPath = '/shelly_files/assets/user_images/';

	const imageGalleries = [
		// { home: $format('tab_home') },
		{ device: $format('tab_devices') },
		{ room: $format('tab_rooms') },
		{ group: $format('tab_groups') },
		{ scene: $format('tab_scenes') },
		{ alarm: $format('tab_alarms') },
		{ avatar: $format('tab_avatars') },
		{ dashboard: $format('tab_dashboards') },
	];

	$: galleriesKeys = [
		$format('tab_devices', { default: 'Devices' }),
		$format('tab_rooms', { default: 'Rooms' }),
		$format('tab_groups', { default: 'Groups' }),
		$format('tab_scenes', { default: 'Scenes' }),
		$format('tab_alarms', { default: 'Alarms' }),
		$format('tab_avatars', { default: 'Avatars' }),
		$format('tab_dashboard', { default: 'Dashboard' }),
	];

	const removeImageDataReset = {
		image: undefined,
		galleryIndex: null,
		galleryType: undefined,
	};

	let showModal = false;
	let galleryCollapses = [...imageGalleries.map((e) => Object.values(e)[0])].map((galleryName) => {
		return {
			title: galleryName,
			expanded: false,
			list: [],
			isLoading: false,
		};
	});

	let removeImageData = {
		...removeImageDataReset,
	};

	const closeModal = () => {
		showModal = false;
	};

	const clearSelection = () => {
		removeImageData = {
			...removeImageDataReset,
		};
	};

	async function removeImage() {
		const imgPath = removeImageData.image.filepath;

		closeModal();

		const res = await postReq('/user_files/delete_image', {
			img_path: imgPath,
		});
		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_title_picture_deleted', {
					default: 'Picture deleted successfully.',
				}),
			});

			// Ivo: Deleting profile image helper.
			deleteImageFromStore(removeImageData);

			// Update gallery localy first, to remove image instantly
			galleryCollapses[removeImageData.galleryIndex] = {
				...galleryCollapses[removeImageData.galleryIndex],
				list: galleryCollapses[removeImageData.galleryIndex].list.filter(
					(img) => img.filepath !== removeImageData.image.filepath,
				),
			};

			let imgList = await handleRequest(Object.keys(removeImageData.galleryType)[0]);

			galleryCollapses[removeImageData.galleryIndex] = {
				...galleryCollapses[removeImageData.galleryIndex],
				list: imgList,
			};

			const imgUrl = $user_data.user_api_url + userImagesPath + imgPath;

			window.URL.revokeObjectURL(imgUrl);

			const imgThumbPath = removeImageData.image?.thumb;

			if (imgThumbPath) {
				const imgThumbUrl = $user_data.user_api_url + userImagesPath + imgThumbPath;

				window.URL.revokeObjectURL(imgThumbUrl);
			}
		}

		clearSelection();
	}

	const onExpandCollapse = async (index: number) => {
		const galleryItem = galleryCollapses[index];
		let expanded = !galleryItem.expanded;
		galleryCollapses[index] = {
			...galleryItem,
			expanded: expanded,
			isLoading: expanded,
		};

		if (expanded) {
			// Get gallery images on expand
			let imgList = await handleRequest(Object.keys(imageGalleries[index])[0]);
			galleryCollapses[index] = {
				...galleryCollapses[index],
				isLoading: false,
				list: imgList,
			};
		}
	};

	const onDeleteImage = (image, index) => {
		showModal = true;
		removeImageData = {
			image: image,
			galleryType: imageGalleries[index],
			galleryIndex: index,
		};
	};
</script>

<Stack gap="s">
	{#each galleryCollapses as gallery, i}
		<Collapse
			id="collapse-w0p3emb"
			title={capFirstChar(galleriesKeys[i])}
			expanded={gallery.expanded}
			on:click={() => {
				onExpandCollapse(i);
			}}
		>
			{#if gallery.isLoading}
				<div class="d-flex justify-content-center">
					<Loader size="m" />
				</div>
			{:else if gallery.expanded && !gallery.isLoading}
				{#if gallery.list.length > 0}
					<CardGrid sm={80} noPaddingBottom>
						{#each gallery.list as g}
							<SelectCard
								cardBgColor={$showNewLayout ? 'color-1' : undefined}
								image={$user_data.user_api_url + userImagesPath + g.filepath}
								size="s"
							>
								<IconButton
									aria-label={$format('button_delete_image', {
										default: 'Delete image',
									})}
									slot="customAction"
									icon="fas fa-trash"
									variant="danger"
									on:click={() => {
										onDeleteImage(g, i);
									}}
								/>
							</SelectCard>
						{/each}
					</CardGrid>
				{:else}
					<Alert type="info" heading={$format('no_images', { default: 'No Images' })} />
				{/if}
			{/if}
		</Collapse>
	{/each}
</Stack>
<Modal
	{showModal}
	title={$format('delete_image_title', { default: 'Delete Image?' })}
	on:close={closeModal}
	class="justify-content-center "
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					image={$user_data.user_api_url + userImagesPath + removeImageData.image.filepath}
					size="xl"
				/>
			</div>
			<Text>
				{$format('delete_img_msg', {
					default:
						'Deleting this image will permanently remove it from your account. Once deleted, you won’t be able to find it in your uploaded pictures.',
				})}
			</Text>
		</Container>
	</svelte:fragment>
	<svelte:fragment slot="footer">
		<div class="d-flex justify-content-end">
			<Button
				variant="secondary"
				on:click={closeModal}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				on:click={removeImage}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
			>
				{$format('delete_image_button', { default: 'Delete Image' })}
			</Button>
		</div>
	</svelte:fragment>
</Modal>
